import { BillingIntervalEnum, HourEnum, WEEKDAYS, WeekNumberEnum } from './enums';
import {
  BusinessHoursType,
  DocumentType,
  EnrollmentType,
  InvitationRecipientType,
  InvoicePreview,
  ScheduledDayType,
  TuitionAndFeesType,
} from './types';

import { DayEnum, FileDocument } from '@wonderschool/common-base-types';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { Timestamp } from 'firebase/firestore';
import { DiscountAmountType } from '../helpers/invoices';
import {
  DAY_ENUM_TO_DAYJS_DAY,
  DAY_ENUM_TO_LEGACY_MAP,
  DOCUMENTS_EMPTY,
  INVITATION_RECIPIENTS_EMPTY,
  LEGACY_TO_DAY_ENUM_MAP,
  SCHEDULED_DAYS_EMPTY,
  TUITION_AND_FEES_EMPTY,
} from './consts';

dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);

const getPeriodEnd = (start: Date, interval: BillingIntervalEnum): Date => {
  if (interval === BillingIntervalEnum.MONTHLY) {
    return dayjs(start).endOf('month').toDate();
  }
  if (interval === BillingIntervalEnum.WEEKLY) {
    const daysUntilSunday = (0 - dayjs(start).day() + 7) % 7;
    return dayjs(start).add(daysUntilSunday, 'day').toDate();
  }
  if (interval === BillingIntervalEnum.BIWEEKLY) {
    const daysUntilSunday = (0 - dayjs(start).day() + 7) % 7;
    return dayjs(start)
      .add(daysUntilSunday + 7, 'day')
      .toDate();
  }
  return start;
};

const getNextPeriodStart = (end: Date, interval: BillingIntervalEnum): Date => {
  if (interval === BillingIntervalEnum.MONTHLY) {
    return dayjs(end).add(1, 'day').startOf('month').toDate();
  }
  if (interval === BillingIntervalEnum.WEEKLY) {
    return dayjs(end).add(1, 'day').toDate();
  }
  if (interval === BillingIntervalEnum.BIWEEKLY) {
    return dayjs(end).add(1, 'day').toDate();
  }
  return end;
};

const getNextDueDate = (
  currentDue: Date,
  interval: BillingIntervalEnum,
  initialDueDate: string,
  billFor: 'Prior' | 'Next'
): Date => {
  if (interval === BillingIntervalEnum.MONTHLY) {
    const dayOfMonth = parseInt(initialDueDate);
    return dayjs(currentDue).add(1, 'month').date(dayOfMonth).toDate();
  }
  if (interval === BillingIntervalEnum.WEEKLY) {
    const dayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
    const dayOfWeek = dayMap[initialDueDate.toLowerCase()];
    if (billFor === 'Prior') {
      return dayjs(currentDue).add(7, 'day').day(dayOfWeek).toDate();
    }
    return dayjs(currentDue).add(7, 'day').day(dayOfWeek).toDate();
  }
  if (interval === BillingIntervalEnum.BIWEEKLY) {
    return dayjs(currentDue).add(14, 'day').toDate();
  }
  return currentDue;
};

const getInitialDueDate = (
  billingInterval: BillingIntervalEnum,
  start: Date,
  dueDate: string,
  billFor: 'Prior' | 'Next'
) => {
  let initialDue: dayjs.Dayjs;
  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    if (billFor === 'Prior') {
      const dayOfMonth = parseInt(dueDate);
      initialDue = dayjs(start).date(dayOfMonth).add(1, 'month');
    } else {
      const dayOfMonth = parseInt(dueDate);
      initialDue = dayjs(start).date(dayOfMonth);
    }

    if (initialDue.isBefore(dayjs(start))) {
      return dayjs(start).toDate();
    }
  } else if (billingInterval === BillingIntervalEnum.WEEKLY) {
    const dayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
    const dayOfWeek = dayMap[dueDate.toLowerCase()];
    if (billFor === 'Prior') {
      initialDue = dayjs(start).day(dayOfWeek).add(7, 'day');
    } else {
      initialDue = dayjs(start).day(dayOfWeek);
    }

    if (initialDue.isBefore(dayjs(start))) {
      return dayjs(start).toDate();
    }
  } else {
    if (billFor === 'Prior') {
      initialDue = dayjs(dueDate).add(14, 'day');
    } else {
      initialDue = dayjs(dueDate);
    }
  }
  return initialDue.toDate();
};

const getProratedDueDate = (
  billingInterval: BillingIntervalEnum,
  billFor: 'Prior' | 'Next',
  dueDate: string,
  enrollmentDate: Date
) => {
  if (billFor === 'Prior') {
    if (billingInterval === BillingIntervalEnum.MONTHLY) {
      const dayOfMonth = parseInt(dueDate);
      const proratedDueDate = dayjs(enrollmentDate).date(dayOfMonth);
      if (proratedDueDate.isBefore(enrollmentDate)) {
        return enrollmentDate;
      }
      return proratedDueDate.toDate();
    }
    if (billingInterval === BillingIntervalEnum.WEEKLY) {
      const dayMap = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
      const dayOfWeek = dayMap[dueDate.toLowerCase()];
      return dayjs(enrollmentDate).day(dayOfWeek).toDate();
    }
  }

  return enrollmentDate;
};

const getNextInvoices = (
  planStartDate: Date,
  tuition: number,
  billingInterval: BillingIntervalEnum,
  dueDate: string | undefined,
  shouldProrate: boolean,
  proratedAmount: number,
  billFor: 'Prior' | 'Next' = 'Next'
): InvoicePreview[] => {
  if (!dueDate) return [];
  const invoices: InvoicePreview[] = [];

  let start = planStartDate;

  const prorateEnd = getPeriodEnd(planStartDate, billingInterval);
  const prorateStart = planStartDate;
  invoices.push({
    label: dayjs(prorateStart).format('MMMM'),
    serviceStart: prorateStart,
    serviceEnd: prorateEnd,
    dueDate: getProratedDueDate(billingInterval, billFor, dueDate, planStartDate),
    amount: shouldProrate ? proratedAmount : tuition,
    isProrated: shouldProrate,
    billingInterval,
  });

  start = getNextPeriodStart(prorateEnd, billingInterval);
  let due: Date = getInitialDueDate(billingInterval, planStartDate, dueDate, billFor);

  while (invoices.length < 3) {
    const end = getPeriodEnd(start, billingInterval);
    invoices.push({
      label: dayjs(start).format('MMMM'),
      serviceStart: start,
      serviceEnd: end,
      dueDate: due,
      amount: tuition,
      isProrated: false,
      billingInterval,
    });
    start = getNextPeriodStart(end, billingInterval);
    due = getNextDueDate(due, billingInterval, dueDate, billFor);
  }

  return invoices;
};

function createInvitationRecipientsFromFamily(student: any): InvitationRecipientType[] {
  const family = student?.family;
  if (!family) return INVITATION_RECIPIENTS_EMPTY;

  const invitationRecipients: InvitationRecipientType[] = [];

  Object.keys(family).forEach((key: string) => {
    const contact = family[key];
    invitationRecipients.push({
      contactId: key,
      uid: contact.uid ?? null,
      email: contact.email ?? null,
      displayName: contact.displayName ?? null,
      phone: contact.phone ?? null,
    });
  });
  return invitationRecipients;
}

// if the recipients are already defined on the enrollment, use those
// otherwise, create them from the family
function getInvitationRecipients(student: any): InvitationRecipientType[] {
  return student?.enrollment?.invitationRecipients ?? INVITATION_RECIPIENTS_EMPTY;
}

function getDocuments(student: any): DocumentType[] {
  return student?.enrollment?.documents ?? DOCUMENTS_EMPTY;
}

// Maps documents picked in the document picker to the enrollment-packet shape
// saved on the student. fileURL is optional on file-service documents, so the
// url key is included only when one actually exists — writing url: null into
// the student's enrollment packet broke consumers of the packet (CCMS-8064).
function toEnrollmentDocuments(selectedDocuments: FileDocument[]): DocumentType[] {
  return selectedDocuments.map((document) => ({
    id: document.id,
    name: document.name,
    ...(document.fileURL ? { url: document.fileURL } : {}),
  }));
}

function getScheduledDays(student: any, defaultBusinessHours: BusinessHoursType): ScheduledDayType[] {
  const enrollment: EnrollmentType = student?.enrollment;
  const studentSchedule: string[] = student?.schedule;

  if (enrollment?.scheduledDays) {
    return enrollment.scheduledDays;
  } else if (studentSchedule && Array.isArray(studentSchedule)) {
    // legacy case
    return studentSchedule.map((day: string) => {
      return {
        day: LEGACY_TO_DAY_ENUM_MAP[day],
        startTime: defaultBusinessHours.startTime,
        endTime: defaultBusinessHours.endTime,
      };
    });
  }
  return SCHEDULED_DAYS_EMPTY;
}

function getScheduledDaysAbbreviated(student: any): string[] {
  if (!student.enrollment?.scheduledDays) {
    return student.schedule ?? [];
  } else if (student.enrollment?.scheduledDays) {
    return student.enrollment.scheduledDays.map(
      (scheduledDay: ScheduledDayType) => DAY_ENUM_TO_LEGACY_MAP[scheduledDay.day]
    );
  } else return [];
}

function getTuitionAndFees(student: any): TuitionAndFeesType {
  const enrollment = student?.enrollment;
  const studentTuitionAndFees = student?.tuitionAndFees;

  if (enrollment?.tuitionAndFees) {
    return enrollment.tuitionAndFees;
  } else if (studentTuitionAndFees) {
    // legacy case
    return studentTuitionAndFees;
  } else {
    return TUITION_AND_FEES_EMPTY;
  }
}

function formatHourForDisplay(hour: HourEnum): string {
  const hourSplit: string[] = hour.split(':');
  const hours = parseInt(hourSplit[0]);
  const minutes = parseInt(hourSplit[1]);
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hours12 = hours % 12;
  const hours12Display = hours12 === 0 ? 12 : hours12;
  const minutesDisplay = minutes < 10 ? `0${minutes}` : minutes;
  return `${hours12Display}:${minutesDisplay} ${ampm}`;
}

function calculateNextInvoiceDate(
  firstInvoiceDate: Timestamp | undefined,
  interval: BillingIntervalEnum | undefined,
  chargeDate: string,
  billFor: 'Prior' | 'Next',
  startDate: number
): Date | undefined {
  if (!firstInvoiceDate || !interval) return undefined;
  const firstInvoiceDay = dayjs.unix(firstInvoiceDate.seconds).utc();
  const enrollmentDay = dayjs.utc(startDate);
  let dueDate: dayjs.Dayjs;
  if (interval === BillingIntervalEnum.WEEKLY) {
    const selectedDayIndex = WEEKDAYS.indexOf(chargeDate as DayEnum);
    const convertedDayIndex = convertWeekdayToDayjsDay(selectedDayIndex);
    const chargeDayThisWeek = firstInvoiceDay.day(convertedDayIndex);
    if (billFor === 'Next') {
      dueDate = chargeDayThisWeek.isSameOrBefore(firstInvoiceDay)
        ? chargeDayThisWeek.add(1, 'week')
        : chargeDayThisWeek;
    } else {
      dueDate = chargeDayThisWeek.add(1, 'week');
    }
  } else if (interval === BillingIntervalEnum.BIWEEKLY) {
    const chargeDay = dayjs(chargeDate, 'YYYY-MM-DD');
    const weekOfMonth = Math.ceil(chargeDay.date() / 7);
    const weekNumber = weekOfMonth % 2 === 0 ? WeekNumberEnum.EVEN : WeekNumberEnum.ODD;

    const { relevantDays } = getRelevantDays(enrollmentDay, chargeDate);

    // Find the next billing date after firstInvoiceDay
    let nextBillingDate: dayjs.Dayjs | undefined;
    if (billFor === 'Next') {
      nextBillingDate = relevantDays.find((date) => date && date.isAfter(firstInvoiceDay));
      if (!nextBillingDate) {
        // If no dates found in current month, get the first occurrence in next month
        const nextMonth = firstInvoiceDay.add(1, 'month').startOf('month');
        nextBillingDate = nextMonth.day(chargeDay.day());
        if (weekNumber === WeekNumberEnum.EVEN) {
          nextBillingDate = nextBillingDate.add(1, 'week');
        }
      }
    } else {
      nextBillingDate = relevantDays.find((date) => date && date.isAfter(firstInvoiceDay));
      if (!nextBillingDate) {
        nextBillingDate = firstInvoiceDay.add(2, 'week');
      }
    }

    dueDate = nextBillingDate;
  } else {
    const chargeDay = firstInvoiceDay.set('date', parseInt(chargeDate));
    dueDate = chargeDay.isSameOrBefore(firstInvoiceDay) ? chargeDay.add(1, 'month') : chargeDay;
  }

  if (interval === BillingIntervalEnum.MONTHLY && dueDate.isBefore(enrollmentDay)) {
    return enrollmentDay.toDate();
  }

  return dueDate.toDate();
}

function getNextInvoiceDate(
  dueDate: Timestamp | undefined,
  billingInterval: BillingIntervalEnum | undefined,
  proratedAmount = 0,
  chargeDate: string,
  billFor: 'Prior' | 'Next',
  startDate: number
) {
  if (!dueDate) return undefined;
  const nextInvoiceDate =
    proratedAmount > 0
      ? calculateNextInvoiceDate(dueDate, billingInterval, chargeDate, billFor, startDate)
      : dayjs.unix(dueDate.seconds).utc().toDate();

  return nextInvoiceDate;
}

function countSessions(periodStart: Date, periodEnd: Date, weekdays: number[]) {
  const start = dayjs(periodStart);
  const end = dayjs(periodEnd);

  // Get zero-based day of the week for start and end dates
  const firstWeekday = start.day();
  const lastWeekday = end.day();

  // Calculate the difference in days and add 1 for inclusive count
  const days = end.diff(start, 'day') + 1;

  // Calculate the number of complete weeks
  const completeWeeks = Math.floor((days - (7 - firstWeekday) - (lastWeekday + 1)) / 7);

  // Calculate sessions in the first and last weeks
  const firstWeekSessions = weekdays.filter((day) => day >= firstWeekday).length || 0;
  const lastWeekSessions = weekdays.filter((day) => day <= lastWeekday).length || 0;

  return Math.trunc(completeWeeks * weekdays.length + firstWeekSessions + lastWeekSessions);
}

function getServicePeriodEndDate(startDate: Date, billingInterval: BillingIntervalEnum): Date {
  const start = dayjs(startDate);

  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    return start.endOf('month').toDate();
  }
  if (billingInterval === BillingIntervalEnum.BIWEEKLY) {
    const daysUntilSunday = (0 - start.day() + 7) % 7;
    return start.add(daysUntilSunday + 7, 'day').toDate();
  }

  const daysUntilSunday = (0 - start.day() + 7) % 7;
  return start.add(daysUntilSunday, 'day').toDate();
}

function calculateProrateAmount(
  weekdays: number[],
  billBeginsOn: Date | null,
  billEndsOn: Date | null,
  rangeBeginsOn: Date | null,
  rangeEndsOn: Date | null,
  amount: number,
  billingInterval: BillingIntervalEnum
) {
  if (weekdays.length === 0 || !billBeginsOn || !billEndsOn || !rangeBeginsOn || !rangeEndsOn || !amount) return 0;
  const adjustedBillEndsOn = getServicePeriodEndDate(billBeginsOn, billingInterval);
  const adjustedRangeEndsOn = getServicePeriodEndDate(rangeBeginsOn, billingInterval);

  const schoolSessions = countSessions(billBeginsOn, adjustedBillEndsOn, weekdays);
  const attendedSessions = countSessions(rangeBeginsOn, adjustedRangeEndsOn, weekdays);

  const ratio = attendedSessions === 0 ? 0 : attendedSessions / schoolSessions;
  return Math.round(amount * ratio);
}

function convertWeekdayToDayjsDay(weekdayIndex: number): number {
  return weekdayIndex === 6 ? 0 : weekdayIndex + 1;
}

function isEnrollmentDateSameAsChargeDate(
  chargeDate: string,
  billingInterval: BillingIntervalEnum,
  enrollmentDate: Date
): boolean {
  const enrollmentDay = dayjs(enrollmentDate);

  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    // For monthly, check if enrollment day matches charge date
    const chargeDayOfMonth = parseInt(chargeDate);
    return enrollmentDay.date() === chargeDayOfMonth;
  } else if (billingInterval === BillingIntervalEnum.BIWEEKLY) {
    const { relevantDays } = getRelevantDays(enrollmentDay, chargeDate);

    return relevantDays.some((day) => day.toString() === enrollmentDay.toString());
  } else {
    // For weekly, check if enrollment weekday matches charge weekday
    const selectedDayIndex = WEEKDAYS.indexOf(chargeDate as DayEnum);
    return enrollmentDay.day() === convertWeekdayToDayjsDay(selectedDayIndex);
  }
}

function calculateFirstInvoiceDue(
  chargeDate: string,
  billingInterval: BillingIntervalEnum,
  enrollmentDate: Date,
  billFor: 'Prior' | 'Next'
): dayjs.Dayjs {
  let enrollmentDay = dayjs(enrollmentDate);

  if (billFor === 'Next' && !enrollmentDay.isBefore(dayjs())) {
    return enrollmentDay;
  }

  if (enrollmentDay.isBefore(dayjs())) {
    enrollmentDay = dayjs();
  }

  let dueDate: dayjs.Dayjs;
  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    const chargeDay = enrollmentDay.set('date', parseInt(chargeDate));
    dueDate = chargeDay.isSameOrBefore(enrollmentDay) ? chargeDay.add(1, 'month') : chargeDay;

    if (dueDate.isBefore(enrollmentDay)) {
      return enrollmentDay;
    }
  } else if (billingInterval === BillingIntervalEnum.BIWEEKLY) {
    const chargeDay = dayjs(chargeDate, 'YYYY-MM-DD');
    const weekOfMonth = Math.ceil(chargeDay.date() / 7);
    const weekNumber = weekOfMonth % 2 === 0 ? WeekNumberEnum.EVEN : WeekNumberEnum.ODD;

    let nextBillingDate = enrollmentDay;
    while (
      nextBillingDate.isBefore(enrollmentDay) ||
      (weekNumber === WeekNumberEnum.EVEN && nextBillingDate.week() % 2 !== 0) ||
      (weekNumber === WeekNumberEnum.ODD && nextBillingDate.week() % 2 !== 1)
    ) {
      nextBillingDate = nextBillingDate.add(1, 'day');
    }
    dueDate = nextBillingDate;
  } else {
    const selectedDayIndex = WEEKDAYS.indexOf(chargeDate as DayEnum);
    const chargeDayThisWeek = enrollmentDay.day(convertWeekdayToDayjsDay(selectedDayIndex));
    dueDate = chargeDayThisWeek.isSameOrBefore(enrollmentDay) ? chargeDayThisWeek.add(1, 'week') : chargeDayThisWeek;
  }

  return dueDate;
}

function calculateStartBillingDate(billingInterval: BillingIntervalEnum, enrollmentDate: Date) {
  const enrollmentDay = dayjs(enrollmentDate);

  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    return enrollmentDay.startOf('month').toDate();
  } else if (billingInterval === BillingIntervalEnum.BIWEEKLY) {
    return enrollmentDay.startOf('week').add(1, 'day').toDate();
  } else {
    return enrollmentDay.startOf('week').add(1, 'day').toDate();
  }
}

function calculateEndBillingDate(chargeDate: string, billingInterval: BillingIntervalEnum, enrollmentDate: Date) {
  if (chargeDate.length === 0) return new Date();
  const enrollmentDay = dayjs.utc(enrollmentDate);

  if (billingInterval === BillingIntervalEnum.MONTHLY) {
    // For monthly, end on last day of month
    return enrollmentDay.endOf('month').toDate();
  } else if (billingInterval === BillingIntervalEnum.BIWEEKLY) {
    // For bi-weekly, end on Sunday of second week
    const daysUntilSunday = (0 - enrollmentDay.day() + 7) % 7;
    return enrollmentDay.add(daysUntilSunday + 7, 'day').toDate();
  } else {
    // For weekly, end on Sunday
    const daysUntilSunday = (0 - enrollmentDay.day() + 7) % 7;
    return enrollmentDay.add(daysUntilSunday, 'day').toDate();
  }
}

function convertScheduledDaysToWeekdayNumbers(scheduledDays: ScheduledDayType[] = []): number[] {
  return scheduledDays.map((scheduleDay) => DAY_ENUM_TO_DAYJS_DAY[scheduleDay.day]).sort((a, b) => a - b);
}

function calculateRecurringInvoiceTotal(fees, tuition = 0) {
  if (!fees?.length) return tuition;

  return fees.reduce((sum, fee) => {
    const amount = fee.amount || 0;
    const isDiscount = fee.type === 'Discount';
    const isCurrency = fee.amountType === DiscountAmountType.CURRENCY;
    let total = isCurrency ? sum + amount : sum + sum * (amount / 100);
    if (isDiscount) {
      total = isCurrency ? sum - amount : sum - sum * (amount / 100);
    }
    return total;
  }, tuition);
}

function shouldCalculateProrateAmount(
  chargeDate: string,
  selectedBillingInterval: BillingIntervalEnum,
  startDate: Date
): boolean {
  if (!chargeDate || !selectedBillingInterval || !startDate) return false;

  const startDay = dayjs(startDate);

  if (selectedBillingInterval === BillingIntervalEnum.MONTHLY) {
    return startDay.date() !== 1;
  } else {
    return startDay.day() !== 1;
  }
}

function getRelevantDays(enrollmentDay: dayjs.Dayjs, chargeDate: string) {
  const chargeDay = dayjs.utc(chargeDate, 'YYYY-MM-DD');
  const weekOfMonth = Math.ceil(chargeDay.date() / 7);
  const weekNumber = weekOfMonth % 2 === 0 ? WeekNumberEnum.EVEN : WeekNumberEnum.ODD;

  // Get the first day of the current month
  const firstDayOfMonth = enrollmentDay.startOf('month');
  // Find the first occurrence of the specified day in the current month
  const targetDays: dayjs.Dayjs[] = [];
  let currentDay = firstDayOfMonth.day(chargeDay.day());
  if (currentDay.month() !== firstDayOfMonth.month()) {
    currentDay = currentDay.add(1, 'week');
  }

  if (enrollmentDay.isBefore(dayjs())) {
    while (currentDay.month() <= enrollmentDay.month()) {
      targetDays.push(currentDay.clone());
      currentDay = currentDay.add(1, 'week');
    }
  } else {
    while (currentDay.month() === enrollmentDay.month()) {
      targetDays.push(currentDay.clone());
      currentDay = currentDay.add(1, 'week');
    }
  }

  // Get the occurrence based on weekNumber (1 = 1st and 3rd, 2 = 2nd and 4th)
  const weekNum = parseInt(weekNumber);
  const relevantDays = targetDays.filter((_d, index) => {
    return index === weekNum - 1 || index === weekNum + 1;
  });

  if (relevantDays.length === 0) {
    relevantDays.push(enrollmentDay);
  }

  return { relevantDays, targetDays };
}

function getPeriodStart(dueDate: Date, interval: BillingIntervalEnum, billFor: 'Prior' | 'Next' = 'Next'): Date {
  if (!interval) return dueDate;

  if (interval === BillingIntervalEnum.MONTHLY) {
    const startOfMonth = dayjs(dueDate).startOf('month');
    if (billFor === 'Prior') {
      return startOfMonth.subtract(1, 'month').toDate();
    }
    return startOfMonth.toDate();
  }

  const daysUntilMonday = (1 - dayjs(dueDate).day() + 7) % 7;
  const startOfWeek = dayjs(dueDate).subtract(daysUntilMonday, 'day');

  if (interval === BillingIntervalEnum.BIWEEKLY) {
    if (billFor === 'Prior') {
      return startOfWeek.subtract(2, 'week').toDate();
    }
    return startOfWeek.subtract(1, 'week').toDate();
  }

  if (billFor === 'Prior') {
    return startOfWeek.subtract(1, 'week').toDate();
  }
  return startOfWeek.toDate();
}

export {
  calculateEndBillingDate,
  calculateFirstInvoiceDue,
  calculateProrateAmount,
  calculateRecurringInvoiceTotal,
  calculateStartBillingDate,
  convertScheduledDaysToWeekdayNumbers,
  createInvitationRecipientsFromFamily,
  formatHourForDisplay,
  getDocuments,
  getInvitationRecipients,
  getNextInvoiceDate,
  getNextInvoices,
  getPeriodStart,
  getScheduledDays,
  getScheduledDaysAbbreviated,
  getTuitionAndFees,
  isEnrollmentDateSameAsChargeDate,
  shouldCalculateProrateAmount,
  toEnrollmentDocuments,
};
